import React, { useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { useAppSelector } from "appRedux/hooks"
import { updateSelectedTabIndex } from "appRedux/slices/profileTabsSlice"
import { getStaticAssetUrl } from "appRedux/baseQuery"
import LazyImage from "components/atoms/LazyImage"

type AvatarProps = {
  handle?: string;
  imageSrc: string | File | null;
  width?: string;
  badge?: React.ReactNode | null;
  onClick?: (e: React.MouseEvent) => void;
}

export default function Avatar(props: AvatarProps) {
  const { handle = "", imageSrc, width, badge, onClick } = props

  const selectedTabIndex = useAppSelector((state) => state.profileTabs.selectedTabIndex)

  const containerRef = useRef<HTMLDivElement>(null)

  const dispatch = useDispatch()

  useEffect(() => {
    setAvatarFileAsContainerBackground()
  }, [imageSrc])

  function setAvatarFileAsContainerBackground() {
    if (!containerRef.current) return
    if (imageSrc && typeof imageSrc !== "string") {
      const fileReader = new FileReader()

      fileReader.onload = () => {
        if (!containerRef.current) return
        containerRef.current.style.backgroundImage = `url(${fileReader.result as string})`
        containerRef.current.style.backgroundSize = "cover"
      }

      fileReader.readAsDataURL(imageSrc)
    }
  }

  function handleOnAvatarClick(event: React.MouseEvent) {
    if (handle) {
      resetSelectedProfileTabIndexForTargetUser(handle)
    }
    if (typeof onClick === "function") {
      onClick(event)
    }
  }

  function resetSelectedProfileTabIndexForTargetUser(handle: string) {
    if (!selectedTabIndex.hasOwnProperty(handle)) return
    dispatch(updateSelectedTabIndex({ handle, index: 0 }))
  }

  function avatarIsFile() {
    return imageSrc && typeof imageSrc !== "string" && imageSrc?.size !== 0
  }

  return (
    <Wrapper
      ref={containerRef}
      className={"avatar"}
      style={{
        width: width || "100%",
        height: width || "100%"
      }}
      onClick={handleOnAvatarClick}
    >
      {badge && <BadgeWrapper>{badge}</BadgeWrapper>}
      <AvatarWrapper>
        {avatarIsFile() ? null : <>
          {imageSrc ? <LazyImage
            src={typeof imageSrc === "string" ? getStaticAssetUrl(imageSrc) : null}
            alt="profile-image"
            placeholder="/static/images/placeholder/user-placeholder.png"
          /> : <img id="placeholder" src="/static/images/placeholder/user-placeholder.png" alt="avatar"/>}
        </>}
      </AvatarWrapper>
    </Wrapper>
  )
}

const Wrapper = styled("div")(({ theme }) => ({
  flexShrink: 0,
  aspectRatio: 1,
  borderRadius: "50%",
  backgroundColor: theme.palette.gray["5"],
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",

  "& img#placeholder": {
    width: "24px",
    height: "24px"
  }
}))

const AvatarWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  flexShrink: 0,
  aspectRatio: 1,
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  position: "relative",

  "& svg": {
    stroke: theme.palette.gray["9"]
  }
}))


const BadgeWrapper = styled(Stack)(({ theme }) => ({
  width: "22px",
  height: "22px",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  top: "55%",
  right: "45%",
  zIndex: 1
}))
